import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, GripVertical, Plus } from 'lucide-react';
import { TableContent } from '../LayoutPage';
import { useAds, useSetAdsOrder, adOrderChanges } from '../../hooks/useAds';
import type { Ad } from '../../hooks/useAds';
import { useArticles } from '../../hooks/useArticles';
import { CmsStateBadge } from '../../components/cms/CmsStateBadge';
import { CmsThumb } from '../../components/cms/CmsThumb';
import { useT } from '../../i18n/admin';
import { useFdCan } from '../../hooks/useFdCan';
import { adHint, cmsState, dayOf, moveId, todayInBusinessClock } from '../../utils/cmsState';

/** Swipe order of the carousel: priority 1 first, then by id. */
const bySwipeOrder = (a: Ad, b: Ad) => (a.priority || Number.MAX_SAFE_INTEGER) - (b.priority || Number.MAX_SAFE_INTEGER) || (a.id ?? 0) - (b.id ?? 0);

export const Ads = () => {
  const { ads, isLoading } = useAds();
  const { articles } = useArticles();
  const { fmt } = useT();
  const navigate = useNavigate();
  const setOrder = useSetAdsOrder();
  const canCreate = useFdCan('cms', 'create');
  const canEdit = useFdCan('cms', 'edit');

  const [dragId, setDragId] = useState<number | null>(null);

  const today = todayInBusinessClock();
  const articleTitle = useMemo(() => new Map(articles.map((a) => [String(a.id), a.title_en])), [articles]);

  const { live, off } = useMemo(() => {
    const sorted = [...ads].sort(bySwipeOrder);
    const withState = sorted.map((ad) => ({ ad, state: cmsState(ad, today) }));
    return {
      live: withState.filter((r) => r.state === 'published'),
      // Not in the app: scheduled first (soonest), then drafts, then expired (most recent first).
      off: withState.filter((r) => r.state !== 'published').sort((a, b) => {
        const rank = { scheduled: 0, draft: 1, expired: 2, published: 3 } as const;
        return rank[a.state] - rank[b.state] || (dayOf(b.ad.publish_date) || '').localeCompare(dayOf(a.ad.publish_date) || '');
      }),
    };
  }, [ads, today]);

  const liveIds = live.map((r) => r.ad.id!);
  const reorder = (from: number, to: number) => {
    const next = moveId(liveIds, from, to);
    if (next === liveIds) return;
    const changes = adOrderChanges(ads, next);
    if (changes.length) setOrder.mutate(changes);
  };

  const range = (ad: Ad) => [fmt.date(dayOf(ad.publish_date)), fmt.date(dayOf(ad.expiration_date))].filter(Boolean).join(' – ') || '-';
  const opens = (ad: Ad) => {
    const o = ad.action_link_object;
    if (!o?.action) return '-';
    if (o.action === 'article') return articleTitle.get(String(o.id)) || `Article ${o.id ?? ''}`.trim();
    if (o.action === 'business') return `Business ${o.id ?? ''}`.trim();
    return 'Link';
  };

  const row = (ad: Ad, state: ReturnType<typeof cmsState>, index?: number) => {
    const hint = adHint(ad, today);
    const dimmed = state === 'expired' || state === 'draft';
    return (
      <tr
        key={ad.id}
        draggable={canEdit && index !== undefined}
        onDragStart={() => setDragId(ad.id!)}
        onDragOver={(e) => { if (dragId !== null && index !== undefined) e.preventDefault(); }}
        onDrop={() => {
          if (dragId !== null && index !== undefined) reorder(liveIds.indexOf(dragId), index);
          setDragId(null);
        }}
        onDragEnd={() => setDragId(null)}
        onClick={() => navigate(`/settings/ads/${ad.id}`)}
        className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${dimmed ? 'opacity-60' : ''}`}
      >
        <td className="w-20 px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {index !== undefined && (
            <span className="flex items-center gap-1 text-gray-500">
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Move ${ad.title_en} (position ${index + 1}): arrow up or down`}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp') { e.preventDefault(); reorder(index, index - 1); }
                    if (e.key === 'ArrowDown') { e.preventDefault(); reorder(index, index + 1); }
                  }}
                  className="cursor-grab border-0 bg-transparent p-0 text-gray-400"
                >
                  <GripVertical size={16} />
                </button>
              )}
              <span className="font-semibold text-gray-900">{index + 1}</span>
            </span>
          )}
        </td>
        <td className="px-2 py-3">
          <div className="flex items-center gap-3">
            <CmsThumb imageKey={ad.art_img_url} width={56} height={56} background={ad.background_color} />
            <div className="min-w-0 max-w-md">
              <div className="font-semibold text-gray-900">{ad.title_en || '-'}</div>
              <div className="line-clamp-2 text-[13px] text-gray-500">{ad.short_description_en}</div>
            </div>
          </div>
        </td>
        <td className="px-2 py-3">{opens(ad)}</td>
        <td className="whitespace-nowrap px-2 py-3">
          {range(ad)}
          {hint && <div className="text-xs text-gray-500">{hint}</div>}
        </td>
        <td className="px-2 py-3"><CmsStateBadge state={state} /></td>
        <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            aria-label={`Preview carousel from ${ad.title_en}`}
            title="Preview carousel"
            onClick={() => navigate('/settings/ads/preview', { state: { adId: ad.id } })}
            className="border-0 bg-transparent p-1 text-gray-500 hover:text-gray-900"
          >
            <Eye size={16} aria-hidden />
          </button>
        </td>
      </tr>
    );
  };

  const head = (
    <thead>
      <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
        <th className="px-4 py-3">#</th>
        <th className="px-2 py-3">Ad</th>
        <th className="px-2 py-3">Opens</th>
        <th className="px-2 py-3">Shows in the app</th>
        <th className="px-2 py-3">Status</th>
        <th className="w-10 px-2 py-3"><span className="sr-only">Preview</span></th>
      </tr>
    </thead>
  );

  return (
    <TableContent>
      <div className="flex h-full flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[13px] font-semibold text-gray-500">CMS</div>
            <h1 className="m-0 text-2xl font-bold text-gray-900">Ads</h1>
            <p className="m-0 mt-1 text-sm text-gray-500">The Featured carousel on the app Home · drag to change the order</p>
          </div>
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={() => navigate('/settings/ads/preview')}
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700"
            >
              <Eye size={16} aria-hidden />
              Preview carousel
            </button>
            {canCreate && (
              <button
                type="button"
                onClick={() => navigate('/settings/ads/new')}
                className="flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white"
                style={{ background: 'var(--tenant-primary, #111827)' }}
              >
                <Plus size={16} />
                Add ad
              </button>
            )}
          </div>
        </div>

        {!isLoading && ads.length === 0 ? (
          <div className="rounded-lg bg-white p-12 text-center text-gray-500 shadow-sm">
            <p className="m-0 font-medium">No ads found</p>
            {canCreate && <p className="m-0 mt-2 text-sm">Click "Add ad" to create the first one</p>}
          </div>
        ) : (
          <div className="flex-1 overflow-auto rounded-lg bg-white shadow-sm">
            <div className="px-4 pt-4">
              <h2 className="m-0 text-base font-semibold">In the app now · {live.length}</h2>
              <p className="m-0 text-[13px] text-gray-500">Published and inside their dates. The # is the swipe order.</p>
            </div>
            <table className="w-full border-collapse text-sm">
              {head}
              <tbody>{live.map(({ ad, state }, i) => row(ad, state, i))}</tbody>
            </table>
            <div className="px-4 pt-6">
              <h2 className="m-0 text-base font-semibold">Not in the app · {off.length}</h2>
              <p className="m-0 text-[13px] text-gray-500">Drafts, scheduled and expired ads never reach residents.</p>
            </div>
            <table className="w-full border-collapse text-sm">
              <tbody>{off.map(({ ad, state }) => row(ad, state))}</tbody>
            </table>
          </div>
        )}

      </div>
    </TableContent>
  );
};
